import React from "react";
// Customizable Area Start
import {
  Box,
  FormControlLabel,
  Grid,
  Switch,
  Typography,
  styled,
  Button,
} from "@mui/material";
import NavigationMenu from "../../navigationmenu/src/NavigationMenu.web";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import NotificationsOutlinedIcon from "@mui/icons-material/NotificationsOutlined";
import FullPageLoader from "../../../components/src/FullPageLoader.web";
import AppFooter from "../../../components/src/AppFooter.web";
import CustomInfoDialog from "../../../components/src/CustomInfoDialog.web";

interface ITwoFactorContentProps {
  isEnabled: boolean;
  handleToggle2fa: (event: React.SyntheticEvent, checked: boolean) => void;
}

interface INotificationsProps {
  inAppEnabled: boolean;
  chatEnabled: boolean;
  handleNotifications: (
    type: "inApp" | "chat",
  ) => (event: React.SyntheticEvent, checked: boolean) => void;
}
// Customizable Area End

import Settings2Controller, { Props } from "./Settings2Controller";
import UserDetailsModal from "../../../components/src/UserDetailsModal.web";
import OptVerificationModal from "../../../components/src/OtpVerificationModal.web";
import { authIcon, authIcon2 } from "./assets";
import ResetPassword from "../../../components/src/ResetPassword.web";
import GoogleAd from "../../../components/src/GoogleAd.web";

export default class Settings2 extends Settings2Controller {
  constructor(props: Props) {
    super(props);
    // Customizable Area Start
    // Customizable Area End
  }

  // Customizable Area Start
  // Customizable Area End

  setActiveTab = (tab: "account" | "twoFactor" | "notifications") => {
    this.setState({ activeTab: tab });
  };

  renderContent() {
    const { is2FAEnabled, chatEnabled, inAppEnabled } = this.state;
    switch (this.state.activeTab) {
      case "account":
        return (
          <AccountContent
            data-test-id="accountContent"
            handleOpenFirstNameModal={this.handleOpenFirstNameModal}
            handleOpenLastNameModal={this.handleOpenLastNameModal}
            handleOpenEmailModal={this.handleOpenEmailModal}
            handleOpenMobileModal={this.handleOpenMobileModal}
            handleOpenRoleModal={this.handleOpenRoleModal}
            handleOpenResetPasswordModal={this.handleOpenResetPasswordModal}
            userData={{
              firstName: this.state.firstName,
              lastName: this.state.lastName,
              email: this.state.email,
              mobileNumber: this.state.mobileNumber,
              role: this.state.role,
            }}
          />
        );
      case "twoFactor":
        return (
          <TwoFactorContent
            data-test-id="2fa-content"
            isEnabled={is2FAEnabled}
            handleToggle2fa={this.handleToggle2fa}
          />
        );
      case "notifications":
        return (
          <NotificationsContent
            data-test-id="notification-content"
            inAppEnabled={inAppEnabled}
            chatEnabled={chatEnabled}
            handleNotifications={this.handleNotifications}
          />
        );
      default:
        return (
          <AccountContent
            handleOpenFirstNameModal={this.handleOpenFirstNameModal}
            handleOpenLastNameModal={this.handleOpenLastNameModal}
            handleOpenEmailModal={this.handleOpenEmailModal}
            handleOpenMobileModal={this.handleOpenMobileModal}
            handleOpenRoleModal={this.handleOpenRoleModal}
            handleOpenResetPasswordModal={this.handleOpenResetPasswordModal}
            userData={{
              firstName: this.state.firstName,
              lastName: this.state.lastName,
              email: this.state.email,
              mobileNumber: this.state.mobileNumber,
              role: this.state.role,
            }}
          />
        );
    }
  }

  render() {
    return (
      // Customizable Area Start
      <SettingsStyle>
        <NavigationMenu navigation={this.props.navigation} id={""} />
        <FullPageLoader isLoading={this.state.isLoading} />
        <div className="settings-container">
          <div className="settings-menu">
            <div className="settings-title-div">
              <Typography className="settings-title">Settings</Typography>
            </div>
            <Box className="mainTabBox">
              <div
                className={`menu-item ${
                  this.state.activeTab === "account" ? "selected" : ""
                }`}
                onClick={() => this.setActiveTab("account")}
              >
                <div style={{ display: "flex", gap: "10px" }}>
                  <PersonOutlineOutlinedIcon />
                  <div className="titleDiv">
                    <span className="titleTYpography">Account</span>
                    <p className="tabSubtitleText">
                      Settings related to your personal information and account
                      credentials.
                    </p>
                  </div>
                </div>
              </div>
              <div
                data-test-id="2fa-tab"
                className={`menu-item ${
                  this.state.activeTab === "twoFactor" ? "selected" : ""
                }`}
                onClick={() => this.setActiveTab("twoFactor")}
              >
                <div style={{ display: "flex", gap: "10px" }}>
                  {this.state.activeTab === "twoFactor" ? (
                    <img
                      style={{ height: "24px", width: "24px" }}
                      src={authIcon2}
                    />
                  ) : (
                    <img
                      style={{ height: "24px", width: "24px" }}
                      src={authIcon}
                    />
                  )}
                  <div className="titleDiv">
                    {" "}
                    <span className="titleTYpography">
                      Two Factor Authentication
                    </span>
                    <p className="tabSubtitleText">
                      Manage your Authentication and securities.
                    </p>
                  </div>
                </div>
              </div>
              <div
                data-test-id="notifications-tab"
                className={`menu-item ${
                  this.state.activeTab === "notifications" ? "selected" : ""
                }`}
                onClick={() => this.setActiveTab("notifications")}
              >
                <div style={{ display: "flex", gap: "10px" }}>
                  <NotificationsOutlinedIcon />
                  <div className="titleDiv">
                    <span className="titleTYpography">Notifications</span>
                    <p className="tabSubtitleText">
                      All settings related to notifications.
                    </p>
                  </div>
                </div>
              </div>
            </Box>
          </div>

          <div className="account-details">{this.renderContent()}</div>
        </div>
        <GoogleAd responsive format="auto" sx={{ mt: 6 }} slot="8671218196" />
        <AppFooter navigation={this.props.navigation} />
        <UserDetailsModal
          data-test-id="handleCloseFirstNameModal"
          open={this.state.isFirstNameModalOpen}
          onClose={this.handleCloseFirstNameModal}
          title="Edit First Name"
          fieldLabel="First Name"
          fieldPlaceholder="Enter your First Name"
          primaryBtnText="Save"
          secondaryBtnText="Cancel"
          fieldKey="firstName"
          defaultValue={this.state.firstName}
          handleOnClick={this.handleValueUpdate}
          errorMessage={this.state.userInfoError}
        />
        <UserDetailsModal
          data-test-id="handleCloseLastNameModal"
          open={this.state.isLastNameModalOpen}
          onClose={this.handleCloseLastNameModal}
          title="Edit Last Name"
          fieldLabel="Last Name"
          fieldPlaceholder="Enter your Last Name"
          primaryBtnText="Save"
          secondaryBtnText="Cancel"
          fieldKey="lastName"
          handleOnClick={this.handleValueUpdate}
          defaultValue={this.state.lastName}
          errorMessage={this.state.userInfoError}
        />
        <UserDetailsModal
          data-test-id="handleCloseEmailModal"
          open={this.state.isEmailModalOpen}
          onClose={this.handleCloseEmailModal}
          title="Change Email Address"
          fieldLabel="New Email Address"
          fieldPlaceholder="Enter your new email address"
          primaryBtnText="Verify"
          secondaryBtnText="Cancel"
          fieldKey="email"
          handleOnClick={this.handleEmailUpdate}
          defaultValue={this.state.email}
          errorMessage={this.state.emailError}
        />
        <UserDetailsModal
          data-test-id="handleCloseMobileModal"
          open={this.state.isMobileModalOpen}
          onClose={this.handleCloseMobileModal}
          title="Change Mobile Number"
          fieldLabel="New Mobile Number"
          fieldPlaceholder="New Mobile Number"
          primaryBtnText="Verify"
          secondaryBtnText="Cancel"
          fieldKey="mobile"
          handleOnClick={this.handleEmailUpdate}
          isMobileField={this.state.isMobileModalOpen}
          defaultValue={this.state.mobileNumber}
          errorMessage={this.state.emailError}
        />
        <UserDetailsModal
          data-test-id="handleCloseRoleModal"
          open={this.state.isRoleModalOpen}
          onClose={this.handleCloseRoleModal}
          title="Edit Role"
          fieldLabel="New Role"
          fieldPlaceholder="Enter your New Role"
          primaryBtnText="Save"
          secondaryBtnText="Cancel"
          fieldKey="role"
          defaultValue={this.state.role}
          handleOnClick={this.handleValueUpdate}
        />
        <OptVerificationModal
          data-test-id="handleCloseOtpModal"
          open={this.state.isOtpModalOpen}
          onClose={this.handleCloseOtpModal}
          contact={this.state.selectedContact}
          contactType={this.state.contactType}
          onVerifyOtp={this.handleVerifyOtp}
          errorMessage={this.state.otpError}
          onResendOtp={this.handleResendOtp}
        />
        <ResetPassword
          data-test-id="handleCloseResetPasswordModal"
          errorMessage={this.state.resetPasswordError}
          open={this.state.isResetPasswordModalOpen}
          onClose={this.handleCloseResetPasswordModal}
          handleOnClick={(
            newPassword: string,
            confirmPassword: string,
            currentPassword: string,
          ) =>
            this.resetPasswordApi(newPassword, confirmPassword, currentPassword)
          }
        />
        <CustomInfoDialog
          data-test-id="custom-info"
          isOpen={this.state.isInfoDialog}
          infoType={this.state.infoType}
          title={this.state.infoMessage}
          handleClose={this.handleCloseInfoDialog}
        />
      </SettingsStyle>
      // Customizable Area End
    );
  }
}

// Customizable Area Start
const AccountContent = ({
  userData,
  handleOpenFirstNameModal,
  handleOpenLastNameModal,
  handleOpenEmailModal,
  handleOpenMobileModal,
  handleOpenResetPasswordModal,
}: {
  userData: {
    firstName: string;
    lastName: string;
    email: string;
    mobileNumber: string;
    role: string;
  };
  handleOpenFirstNameModal: () => void;
  handleOpenLastNameModal: () => void;
  handleOpenEmailModal: () => void;
  handleOpenMobileModal: () => void;
  handleOpenRoleModal: () => void;
  handleOpenResetPasswordModal: () => void;
}) => {
  return (
    <div className="mainAccountRightContainer">
      <Typography className="accountTitle">Account</Typography>
      <Box sx={{ marginTop: 5 }}>
        <Grid className="innerGrid" container spacing={2}>
          <Grid item>
            <Typography className="acountActionText">
              Account Actions
            </Typography>
          </Grid>
          <Grid className="userDetailsGrid" item xs={10}>
            <Grid container className="userDetails">
              <Grid item xs={6}>
                <Typography className="accountUserTitle">First Name</Typography>
                <Typography className="accUserDetailsText">
                  {userData.firstName}
                </Typography>
              </Grid>
              <Grid item>
                <Button
                  className="editBtn"
                  data-test-id="firstNameEditClick"
                  onClick={handleOpenFirstNameModal}
                >
                  Edit
                </Button>
              </Grid>
            </Grid>
            <Grid container className="userDetails">
              <Grid item xs={6}>
                <Typography className="accountUserTitle">Last Name</Typography>
                <Typography className="accUserDetailsText">
                  {userData.lastName}
                </Typography>
              </Grid>
              <Grid item>
                <Button className="editBtn" onClick={handleOpenLastNameModal}>
                  Edit
                </Button>
              </Grid>
            </Grid>
            <Grid container className="userDetails">
              <Grid item xs={6}>
                <Typography className="accountUserTitle">Email</Typography>
                <Typography className="accUserDetailsText">
                  {userData.email}
                </Typography>
              </Grid>
              <Grid item>
                <Button className="editBtn" onClick={handleOpenEmailModal}>
                  Edit
                </Button>
              </Grid>
            </Grid>
            <Grid container className="userDetails">
              <Grid item xs={6}>
                <Typography className="accountUserTitle">
                  Mobile Number
                </Typography>
                <Typography className="accUserDetailsText">
                  {userData.mobileNumber && `+91 ${userData.mobileNumber}`}
                </Typography>
              </Grid>
              <Grid item>
                <Button className="editBtn" onClick={handleOpenMobileModal}>
                  Edit
                </Button>
              </Grid>
            </Grid>
            <Grid container className="userDetails">
              <Grid item xs={6}>
                <Typography className="accountUserTitle">Password</Typography>
              </Grid>
              <Grid item>
                <Button
                  className="editBtn"
                  onClick={handleOpenResetPasswordModal}
                >
                  Reset
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Box>
    </div>
  );
};

const IOSSwitch = styled(Switch)(({ theme }) => ({
  width: 42,
  height: 26,
  padding: 0,
  margin: theme.spacing(1),
  "& .MuiSwitch-switchBase": {
    padding: 1,
    "&.Mui-checked": {
      transform: "translateX(16px)",
      color: theme.palette.common.white,
      "& + .MuiSwitch-track": {
        backgroundColor: "#652784",
        opacity: 1,
        border: "none",
      },
    },
    "&.Mui-focusVisible .MuiSwitch-thumb": {
      color: "#652784",
      border: "6px solid #fff",
    },
  },
  "& .MuiSwitch-thumb": {
    width: 24,
    height: 24,
  },
  "& .MuiSwitch-track": {
    borderRadius: 13,
    border: `1px solid ${theme.palette.grey[400]}`,
    backgroundColor: theme.palette.grey[50],
    opacity: 1,
    transition: theme.transitions.create(["background-color", "border"]),
  },
}));

const TwoFactorContent = ({
  isEnabled,
  handleToggle2fa,
}: ITwoFactorContentProps) => (
  <Box className="mainAccountRightContainer">
    <Typography className="accountTitle">Two Factor Authentication</Typography>
    <Box>
      <Box className="innerAuthBox">
        <Box>
          <Typography className="innerAuthBoxTitle">
            Two Factor Authentication
          </Typography>
          <Typography className="innerAuthBoxSubTitle">
            Manage your Authentication and securities.
          </Typography>
        </Box>
        <FormControlLabel
          control={<IOSSwitch name="checkedB" />}
          label=""
          data-test-id="2fa-radio"
          checked={isEnabled}
          onChange={handleToggle2fa}
        />
      </Box>
    </Box>
  </Box>
);

const NotificationsContent = ({
  inAppEnabled,
  chatEnabled,
  handleNotifications,
}: INotificationsProps) => (
  <Box className="mainAccountRightContainer">
    <Typography className="accountTitle">Notifications</Typography>
    <Box className="innerAuthBox">
      <Box>
        <Typography className="innerAuthBoxTitle">
          {" "}
          In App Notifications
        </Typography>
        <Typography className="innerAuthBoxSubTitle">
          Notifications when you receive new notification.
        </Typography>
      </Box>
      <FormControlLabel
        control={<IOSSwitch name="inApp" />}
        label=""
        checked={inAppEnabled}
        onChange={handleNotifications("inApp")}
      />
    </Box>
    <Box className="innerAuthBox">
      <Box>
        <Typography className="innerAuthBoxTitle">Chats</Typography>
        <Typography className="innerAuthBoxSubTitle">
          Notifications when you receive new messages.
        </Typography>
      </Box>
      <FormControlLabel
        data-test-id="notification-radio"
        control={<IOSSwitch name="chat" />}
        label=""
        checked={chatEnabled}
        onChange={handleNotifications("chat")}
      />
    </Box>
  </Box>
);
// Customizable Area End

// Customizable Area Start
const SettingsStyle = styled(Box)({
  "& .settings-container": {
    display: "flex",
    padding: "60px",
    marginLeft: "20px",
    marginRight: "20px",
    backgroundColor: "#f0f4ff",
    "@media (max-width: 900px)": {
      padding: "20px",
    },
  },
  "& .settings-title-div": {
    padding: "10px",
    marginBottom: "35px",
  },
  "& .settings-title": {
    color: "#010101",
    fontWeight: 600,
    fontSize: "28px",
    lineHeight: "34.13px",
    letterSpacing: "-0.15px",
  },
  "& .mainTabBox": {
    display: "flex",
    gap: "20px",
    flexDirection: "column",
  },
  "& .settings-menu": {
    flex: 1,
    maxWidth: "400px",
    backgroundColor: "#fff",
    borderRadius: "8px",
    padding: "40px",
    boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
    "@media (max-width: 900px)": {
      padding: "20px",
    },
  },
  "& .titleDiv": {
    display: "flex",
    alignItems: "flex-start",
    flexDirection: "column",
  },
  "& .titleTYpography": {
    fontWeight: 600,
    fontSize: "16px",
    lineHeight: "24px",
    letterSpacing: "0%",
  },
  "& .titleTYpography .selected": {
    fontWeight: 600,
    fontSize: "16px",
    lineHeight: "24px",
    letterSpacing: "0%",
  },
  "& .tabSubtitleText": {
    fontWeight: 400,
    fontSize: "14px",
    lineHeight: "19.69px",
    letterSpacing: "0%",
  },
  "& .menu-item": {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    padding: "10px",
    borderRadius: "8px",
    cursor: "pointer",
    minHeight: "85px",
    transition: "background 0.3s ease",
  },
  "& .menu-item:hover": {
    background: "#ececff",
  },
  "& .menu-item.selected": {
    background: "linear-gradient(135deg, #450066, #16002A)",
    color: "white",
  },
  "& .icon": {
    fontSize: "24px",
  },
  "& .account-details": {
    flex: 2,
    backgroundColor: "#fff",
    borderRadius: "8px",
    padding: "40px",
    boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
    "@media (max-width: 900px)": {
      padding: "20px",
    },
  },
  "& .innerGrid": {
    "@media (min-width: 320px) and (max-width: 640px)": {
      display: "block",
      width: "120%",
    },
  },
  "& .userDetailsGrid": {
    width: "auto",
  },
  "& .mainAccountRightContainer": {
    padding: "20px",
    "@media (min-width: 320px) and (max-width: 640px)": {
      padding: "2px",
    },
  },
  "& .innerAuthBox": {
    marginTop: "40px",
    display: "flex",
    justifyContent: "space-between",
    "@media (min-width: 320px) and (max-width: 440px)": {
      width: "100%",
    },
  },
  "& .innerAuthBoxTitle": {
    fontWeight: 500,
    fontSize: "16px",
    lineHeight: "19.2px",
    letterSpacing: "-0.15px",
    fontFamily: "Montserrat !important",
    color: "#010101",
  },
  "& .innerAuthBoxSubTitle": {
    fontWeight: 500,
    fontSize: "14px",
    lineHeight: "19.36px",
    fontFamily: "Montserrat !important",
    color: "#64748B",
    marginTop: "10px",
  },
  "& .accountTitle": {
    fontWeight: 500,
    fontSize: "24px",
    lineHeight: "32px",
    letterSpacing: "-0.5%",
    color: "#0F172A",
    "@media (max-width: 900px)": {
      fontSize: "22px",
    },
  },
  "& .acountActionText": {
    fontWeight: 500,
    fontSize: "16px",
    lineHeight: "19px",
    letterSpacing: "-0.5%",
    color: "#0F172A",
  },
  "& .accountUserTitle": {
    fontWeight: 500,
    fontSize: "16px",
    lineHeight: "19.5px",
    letterSpacing: "-0.5%",
    color: "#0F172A",
    "@media (min-width: 320px) and (max-width: 640px)": {
      fontSize: "14px",
      lineHeight: "16px",
    },
  },
  "& .userDetails": {
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "20px",
    "@media (min-width: 320px) and (max-width: 640px)": {
      marginBottom: "10px",
    },
  },
  "& .editBtn": {
    textTransform: "none",
    color: "#0D0C22",
    fontWeight: 500,
    fontSize: "14px",
    lineHeight: "22px",
    "@media (min-width: 320px) and (max-width: 640px)": {
      marginBottom: "10px",
    },
  },
  "& .accUserDetailsText": {
    fontWeight: 500,
    fontSize: "14px",
    lineHeight: "22px",
    color: "#64748B",
    "@media (min-width: 320px) and (max-width: 640px)": {
      fontSize: "12px",
      lineHeight: "20px",
    },
  },
  "& .account-item": {
    display: "grid",
    gridTemplateColumns: "1fr 2fr auto",
    alignItems: "center",
    padding: "10px 0",
  },
  "& .detail": {
    color: "#6b7280",
  },
  "& .edit-button": {
    backgroundColor: "transparent",
    color: "#4b0082",
    border: "none",
    cursor: "pointer",
  },
  "& .reset-button": {
    backgroundColor: "transparent",
    color: "#d32f2f",
    border: "none",
    cursor: "pointer",
  },
  "@media (max-width: 768px)": {
    "& .settings-container": {
      flexDirection: "column",
    },
    "& .settings-menu": {
      maxWidth: "100%",
      marginBottom: "20px",
    },
  },
});
// Customizable Area End
